/*
 * ⚠️ 이 파일은 **두 벌 있다.**
 *     tastee_website/src/data/quizTypes.ts   (tastee.kr/quiz — 계정 없는 사람)
 *     tastee_web-main/src/data/quizTypes.ts  (앱 안 환경설정 > 취향분석)
 *
 * 문항이나 점수 계산을 고칠 때 **반드시 양쪽을 같이** 고친다. 한쪽만 고치면
 * 같은 테스트가 다른 결과를 내는데, 오류가 안 나서 아무도 모른다.
 * 저장소가 달라 공유할 방법이 마땅치 않아 이렇게 둔다.
 *
 * 한 군데만 다르다 — import 경로의 '.ts' 확장자를 뗐다. 웹사이트는 Node
 * 스크립트가 직접 읽느라 붙여 두는데, 이쪽 빌드(CRA)는 그걸 거절한다.
 */
/**
 * 입맛 유형 테스트 v2 — 결과 문구 (데이터만).
 *
 * 유형 이름은 항상 8개 중 하나(1위 성향). 특별 조합은 이름을 바꾸지 않고
 * 설명 안에 "그중에서도 당신은 {조합 이름}" 단락으로만 붙는다.
 */
import type { Trait } from "./quiz";

export interface TypeResult {
  trait: Trait;
  name: string;
  oneLiner: string;
  description: string;
  tags: string[];
  restaurant: string;
  /** 결과 이미지. 새 이미지가 나오면 경로만 넣으면 화면에 보인다 (지금은 비워 둠) */
  image?: string;
}

export const TYPES: Record<Trait, TypeResult> = {
  explorer: {
    trait: "explorer",
    name: "남들 모르는 집만 파는 미식계의 은둔 고수",
    oneLiner: "아무도 안 가 본 집? 오히려 좋아",
    description:
      "리뷰가 하나도 없는 집 앞에서 오히려 설레는 사람. 지도 앱 순위보다 골목에서 풍기는 냄새를 더 믿는다. 친구들이 알게 될 즈음엔 이미 몇 번 다녀온 뒤다.",
    tags: ["리뷰0개환영", "골목탐험", "먼저발견"],
    restaurant: "간판 작은 골목 노포",
  },
  regular: {
    trait: "regular",
    name: "단골집 망할까 봐 주기적으로 돈쭐내는 의리파",
    oneLiner: "사장님이 내 주문을 먼저 안다",
    description:
      "마음에 든 집은 끝까지 간다. 새로 생긴 곳보다 검증된 내 단골이 더 좋고, 그 집이 오래 남아 있길 진심으로 바란다. 친구를 데려가는 곳도 결국 내 단골집.",
    tags: ["의리", "늘먹던거", "사장님과아는사이"],
    restaurant: "동네 오래된 단골집",
  },
  value: {
    trait: "value",
    name: '"내 사전에 바가지는 없다" 가성비 헌터',
    oneLiner: "만원의 행복을 전파합니다",
    // 먹BTI v1 RAFP 본문 그대로
    description:
      "검증된 곳에서 다 같이 가성비 좋게 먹는 게 최고. 분석적으로 따지되 함께 먹는 시간 자체를 즐긴다. 가격 대비 만족도 높은 장소를 찾는 천재적 재능.",
    tags: ["가성비전문가", "만원의행복", "팀플레이어", "합리적소셜"],
    restaurant: "기사식당",
  },
  experience: {
    trait: "experience",
    name: "좋은 한 끼엔 지갑을 아끼지 않는 미식가",
    oneLiner: "식감, 온도, 간… 논문 쓸 수 있어",
    description:
      "양보다 완성도를 본다. 셰프가 누구인지, 재료가 어디서 왔는지가 궁금하고, 제대로 된 한 접시라면 가격은 그다음 문제다. 먹어 본 만큼 기준이 높아지는 사람.",
    tags: ["완성도", "셰프의한접시", "특별한날"],
    restaurant: "오마카세·파인다이닝",
  },
  solo: {
    trait: "solo",
    name: "먹고 싶으면 혼자서도 바로 가는 1인 행동파",
    oneLiner: "같이 갈 사람 기다리다 배고파",
    description:
      "먹고 싶은 게 생기면 일정 맞출 사람을 찾지 않는다. 고깃집이든 줄 서는 집이든 혼자 들어가는 데 망설임이 없다. 메뉴도 속도도 내 마음대로, 혼밥은 이 사람에게 자유다.",
    tags: ["혼밥마스터", "바로실행", "내속도대로"],
    restaurant: "1인석 있는 맛집",
  },
  mood: {
    trait: "mood",
    name: "좋은 공간에서 기분 전환하는 분위기 수집가",
    oneLiner: "가기 전에 내부 사진부터 체크",
    description:
      "조명, 음악, 창밖 풍경까지 한 끼의 일부라고 생각한다. 같은 메뉴라도 어디서 먹었느냐가 그날의 기억을 바꾼다. 지친 날엔 맛있는 음식보다 좋은 공간이 먼저 떠오르는 사람.",
    tags: ["공간맛집", "기분전환", "조명맛집"],
    restaurant: "분위기 좋은 레스토랑·카페",
  },
  trend: {
    trait: "trend",
    name: "오픈런 알람 맞춰놓는 핫플 사냥꾼",
    oneLiner: "새 식당 오픈하면 제일 먼저 가줘야 함",
    // 먹BTI v1 ESLP 본문 그대로
    description:
      "새로운 맛집이 열리면 가장 먼저 달려가고, 친구들에게 가장 먼저 공유하는 타입. 당신에게 먹는 건 곧 경험 + 소통. SNS 맛집 게시물의 원천이 바로 당신.",
    tags: ["핫플선발대", "공유본능", "감각중시", "FOMO체질"],
    restaurant: "신상 핫플",
  },
  waiting: {
    trait: "waiting",
    name: "줄 서는 집은 일단 서고 보는 웨이팅 장인",
    oneLiner: "기다림도 맛의 일부다",
    description:
      "남들이 줄 서는 데는 다 이유가 있다고 믿는 사람. 오픈런도, 웨이팅 앱 알림도 익숙하다. 1시간 기다려 먹은 한 입이 더 맛있다는 걸 안다. 검증된 인기에 기꺼이 시간을 쓰는 타입.",
    tags: ["오픈런", "웨이팅앱고수", "검증된인기", "기다림도맛"],
    restaurant: "줄 서는 맛집",
  },
};

export interface Combo {
  primary: Trait;
  secondary: Trait;
  name: string;
  description: string;
}

/** 1위 × 2위가 정확히 일치할 때만 붙는 특별 조합 */
export const COMBOS: Combo[] = [
  {
    primary: "trend",
    secondary: "mood",
    name: "요즘 감성 큐레이터",
    description:
      '요즘 뜨는 곳 중에서도 공간까지 좋은 곳만 골라낸다. 이 사람이 다녀온 곳은 사진만 봐도 가고 싶어진다. 친구들이 "요즘 어디 가?" 하고 먼저 묻는 사람.',
  },
  {
    primary: "experience",
    secondary: "mood",
    name: "음식도 공간도 완벽해야 직성이 풀리는 미식가",
    description:
      '맛있는 것만으로는 부족하다. 접시가 나오는 순서, 조명, 자리 간격까지 맞아야 비로소 "좋은 한 끼"라고 부른다. 기념일 장소는 늘 이 사람에게 묻게 된다.',
  },
  {
    primary: "explorer",
    secondary: "value",
    name: "아무도 모르는 가성비 맛집 발굴 달인",
    description:
      "유명하지 않아도, 싸고 맛있는 집은 귀신같이 찾아낸다. 이 사람이 데려간 골목집은 가격 보고 한 번, 맛 보고 한 번 놀란다. 리스트에 있는 집 대부분이 아직 리뷰가 거의 없다.",
  },
  {
    primary: "regular",
    secondary: "experience",
    name: "메뉴판에 없는 메뉴까지 다 먹어본 찐 단골",
    description:
      '한번 마음에 든 집은 메뉴판 끝까지, 그다음엔 메뉴판 밖까지 간다. 사장님이 "오늘 좋은 거 들어왔어" 하고 먼저 말해 주는 사이. 맛을 아는 단골이라 그 집의 진짜 맛을 가장 잘 안다.',
  },
  {
    primary: "regular",
    secondary: "waiting",
    name: "남들 줄 서는 그 집에 매주 출근하는 사람",
    description:
      "남들은 큰맘 먹고 줄 서는 집을 이 사람은 생활처럼 다닌다. 몇 시에 가야 덜 기다리는지, 어떤 메뉴가 먼저 떨어지는지 다 안다. 기다림까지 루틴이 된 진짜 단골.",
  },
  {
    primary: "regular",
    secondary: "solo",
    name: "나만의 혼밥 아지트를 지키는 사람",
    description:
      "혼자 가도 편한 단골집이 몇 군데 있다. 늘 앉는 자리, 늘 먹는 메뉴가 정해져 있고, 사장님도 굳이 묻지 않는다. 너무 유명해지지 않았으면 하는 집이 하나쯤 있다.",
  },
  {
    primary: "experience",
    secondary: "trend",
    name: "새로 뜬 미식은 먼저 맛보는 트민러",
    description:
      '요즘 화제인 메뉴는 빠짐없이 먹어 보지만, 유행이라고 다 좋다고 하지는 않는다. 먹어 본 만큼 기준이 있어서 "진짜 맛있는 유행"을 골라낸다. 친구들이 이 사람 후기를 보고 갈지 말지 정한다.',
  },
  {
    primary: "value",
    secondary: "solo",
    name: "혼자서도 가성비 맛집만 쏙쏙 찾아다니는 혼밥 고수",
    description:
      "음식 선택의 최적화를 추구하는 타입. 가성비, 동선, 웨이팅 시간까지 고려해서 가장 합리적인 한 끼를 설계한다. 혼자여도 대충 먹지 않는 사람.",
  },
  {
    primary: "experience",
    secondary: "explorer",
    name: "걸어 다니는 맛집 지도",
    description:
      '안 가본 동네의 맛집도 이미 리스트에 있는 사람. 많이 먹어 본 만큼 기준이 확실해서, 처음 가는 곳에서도 실패가 적다. "어디 가지?" 하면 지역별로 바로 답이 나온다.',
  },
];

/** 결과 상단에 붙는 2위 성향 한 줄 */
export const SECOND_LINE: Record<Trait, string> = {
  explorer: "남들 모르는 골목도 기꺼이",
  regular: "한번 꽂히면 끝까지",
  value: "가격 대비 만족은 기본",
  experience: "한 접시라도 제대로",
  solo: "혼자서도 당당하게",
  mood: "공간까지 맛의 일부",
  trend: "요즘 뜨는 건 먼저",
  waiting: "웨이팅도 기꺼이",
};

/** 특별 조합이 아닐 때 설명 하단에 붙는 "숨은 성향" 단락 */
export const SECOND_PARAGRAPH: Record<Trait, string> = {
  explorer:
    "검증된 곳도 좋지만, 가끔은 아무도 리뷰를 안 쓴 골목집 문을 먼저 연다. 남들보다 한발 먼저 발견한 맛집이 꼭 하나쯤은 있는 사람.",
  regular:
    "새로운 곳도 가 보지만 결국 돌아가는 집이 있다. 마음에 든 곳은 메뉴를 하나씩 정복하며 오래 다닌다. 당신의 단골집 리스트는 짧지만 확실하다.",
  value:
    "아무리 좋아도 가격 대비 만족이 안 나오면 두 번은 안 간다. 같은 돈이면 더 맛있게, 같은 맛이면 더 합리적으로 먹는 법을 안다.",
  experience:
    "한 끼를 고를 때 결국 맛의 완성도를 본다. 양보다 질, 특별한 날엔 기꺼이 지갑을 연다. 먹어 본 만큼 기준이 높아지는 타입.",
  solo:
    "먹고 싶은 게 생기면 같이 갈 사람을 기다리지 않는다. 혼자서도 맛집 앞에서 망설이지 않는 사람. 당신에게 혼밥은 외로움이 아니라 자유다.",
  mood:
    "맛만큼 공간도 중요하다. 조명, 음악, 자리까지 마음에 들어야 한 끼가 완성된다. 같은 메뉴라도 어디서 먹었느냐가 기억을 바꾼다고 믿는다.",
  trend:
    '요즘 뭐가 뜨는지는 피드만 봐도 안다. 화제의 메뉴는 늦기 전에 한 번은 먹어 봐야 직성이 풀린다. 친구들 사이에서 "그거 먹어 봤어?"를 제일 먼저 묻는 사람.',
  waiting:
    "검증된 인기라면 기다림도 아깝지 않다. 줄이 길수록 오히려 기대가 커지고, 오래 기다린 한 입은 더 맛있게 느껴진다.",
};

export const findCombo = (primary: Trait, secondary: Trait): Combo | undefined =>
  COMBOS.find((c) => c.primary === primary && c.secondary === secondary);
